import {
  Alert, Anchor, Button, Card, Drawer, Group, List, Modal, MultiSelect, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, Textarea, TextInput, Timeline, Title,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconBan, IconCalendarEvent, IconMail, IconPlus, IconRefresh, IconSearch, IconUserPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../lib/api';
import { STATUS, fmtDate, fmtDateTime } from '../../lib/format';
import { useMe } from '../../lib/me';
import type { AssignmentEvent, Department, DirUser, Row } from '../../lib/types';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';
import { StatusBadge } from '../../ui/StatusBadge';

type Mode = 'departments' | 'users' | 'all';
interface PubCourse { id: number; title: string; version: number }

function AssignDrawer({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const courses = useQuery({ queryKey: ['assign-courses'], queryFn: () => api.get<PubCourse[]>('/admin/assignments/courses'), enabled: opened });
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.get<Department[]>('/admin/departments'), enabled: opened });
  const users = useQuery({ queryKey: ['dir-users'], queryFn: () => api.get<DirUser[]>('/admin/users'), enabled: opened });
  const [courseId, setCourseId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('departments');
  const [deptIds, setDeptIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [due, setDue] = useState<string | null>(dayjs().add(14, 'day').format('YYYY-MM-DD'));
  const active = (users.data ?? []).filter((u) => u.active && !u.anonymized);
  const count = mode === 'all' ? active.length : mode === 'departments' ? active.filter((u) => u.departmentId && deptIds.includes(String(u.departmentId))).length : userIds.length;

  const assign = useMutation({
    mutationFn: () => api.post<{ created: number; skipped: number; skippedUsers: string[] }>('/admin/assignments', {
      courseId: Number(courseId), all: mode === 'all', departmentIds: mode === 'departments' ? deptIds.map(Number) : [], userIds: mode === 'users' ? userIds.map(Number) : [], dueDate: due,
    }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['assignments'] });
      await qc.invalidateQueries({ queryKey: ['summary'] });
      notifications.show({ color: 'teal', title: `Назначено: ${r.created}`, message: r.skipped ? `Пропущено (уже проходят): ${r.skippedUsers.join(', ')}` : 'Сотрудникам отправлены письма о назначении' });
      onClose();
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });

  return (
    <Drawer opened={opened} onClose={onClose} title={<Title order={4}>Назначить курс</Title>} position="right" size="md">
      <Stack>
        <Select label="Курс" placeholder="Опубликованный курс" value={courseId} onChange={setCourseId} data={(courses.data ?? []).map((c) => ({ value: String(c.id), label: `${c.title} (v${c.version})` }))} />
        <SegmentedControl value={mode} onChange={(v) => setMode(v as Mode)} data={[{ value: 'departments', label: 'Подразделениям' }, { value: 'users', label: 'Сотрудникам' }, { value: 'all', label: 'Всем' }]} />
        {mode === 'departments' && <MultiSelect label="Подразделения" value={deptIds} onChange={setDeptIds} searchable data={(departments.data ?? []).map((d) => ({ value: String(d.id), label: `${d.name} (${d._count.users})` }))} />}
        {mode === 'users' && <MultiSelect label="Сотрудники" value={userIds} onChange={setUserIds} searchable data={active.map((u) => ({ value: String(u.id), label: `${u.fullName} · ${u.department ?? '-'}` }))} />}
        <DateInput label="Срок" description="До конца выбранного дня в часовом поясе каждого сотрудника" value={due} onChange={setDue} minDate={dayjs().format('YYYY-MM-DD')} valueFormat="D MMMM YYYY" />
        <Alert variant="light" color="blue" icon={<IconMail size={16} />}>Сотрудники получат письмо о назначении: название курса, срок и адрес платформы, без кнопки входа. За 3 дня до срока придёт напоминание.</Alert>
        <Text size="sm" c="dimmed">Получат назначение: <b>{count}</b>. У кого курс уже активен - будут пропущены.</Text>
        <Button leftSection={<IconUserPlus size={16} />} disabled={!courseId || !due || count === 0} loading={assign.isPending} onClick={() => assign.mutate()}>Назначить</Button>
      </Stack>
    </Drawer>
  );
}

export function AssignmentsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [opened, { open, close }] = useDisclosure(params.get('new') === '1');
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const filter = { courseId: params.get('courseId') ?? '', department: params.get('department') ?? '', status: params.get('status') ?? '', dueFrom: params.get('dueFrom') ?? '', dueTo: params.get('dueTo') ?? '', q };
  const qs = new URLSearchParams(Object.entries(filter).filter(([, v]) => v)).toString();
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n, { replace: true });
  };
  const courses = useQuery({ queryKey: ['assign-courses'], queryFn: () => api.get<PubCourse[]>('/admin/assignments/courses') });
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.get<Department[]>('/admin/departments') });
  const rows = useQuery({ queryKey: ['assignments', qs], queryFn: () => api.get<Row[]>(`/admin/assignments?${qs}`) });

  return (
    <>
      <PageHeader title="Назначения" subtitle="Без баллов: статусы, сроки и события. Перенос, отмена и «+N попыток» - только с причиной." actions={<Button leftSection={<IconPlus size={16} />} onClick={open}>Назначить курс</Button>} />
      <Card withBorder radius="lg" padding="md" mb="md">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput label="Поиск" placeholder="ФИО, email, табельный" leftSection={<IconSearch size={14} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={210} />
          <Select label="Курс" placeholder="Все" clearable value={filter.courseId || null} onChange={(v) => set('courseId', v)} w={220} data={(courses.data ?? []).map((c) => ({ value: String(c.id), label: c.title }))} />
          <Select label="Подразделение" placeholder="Все" clearable value={filter.department || null} onChange={(v) => set('department', v)} w={170} data={(departments.data ?? []).map((d) => d.name)} />
          <Select label="Статус" placeholder="Все" clearable value={filter.status || null} onChange={(v) => set('status', v)} w={210} data={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))} />
          <DatePickerInput type="range" label="Срок" placeholder="Любой" clearable w={230} valueFormat="D MMM YYYY"
            value={[filter.dueFrom ? dayjs(filter.dueFrom).format('YYYY-MM-DD') : null, filter.dueTo ? dayjs(filter.dueTo).format('YYYY-MM-DD') : null]}
            onChange={([from, to]) => {
              const n = new URLSearchParams(params);
              if (from) n.set('dueFrom', dayjs(from).startOf('day').toISOString()); else n.delete('dueFrom');
              if (to) n.set('dueTo', dayjs(to).endOf('day').toISOString()); else n.delete('dueTo');
              setParams(n, { replace: true });
            }} />
        </Group>
      </Card>
      <QueryState loading={rows.isLoading} error={rows.error}>
        {() => (
          <Card withBorder radius="lg" p={0}>
            <Table.ScrollContainer minWidth={1000}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" striped>
                <Table.Thead><Table.Tr><Table.Th>Сотрудник</Table.Th><Table.Th>Курс</Table.Th><Table.Th>Назначен</Table.Th><Table.Th>Срок</Table.Th><Table.Th>Статус</Table.Th><Table.Th>Уроки</Table.Th><Table.Th>Попытки</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {rows.data!.map((r) => (
                    <Table.Tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/assignments/${r.id}`)}>
                      <Table.Td><Text size="sm" fw={500}>{r.fullName}</Text><Text size="xs" c="dimmed">{r.department ?? '-'}{r.employeeId ? ` · ${r.employeeId}` : ''}</Text></Table.Td>
                      <Table.Td><Text size="sm">{r.course}</Text><Text size="xs" c="dimmed">v{r.version}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(r.assignedAt)}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(r.dueAt, r.timezone)}</Text>{r.rescheduled && <Text size="xs" c="dimmed">исходный {fmtDate(r.originalDueAt, r.timezone)}</Text>}</Table.Td>
                      <Table.Td miw={180}><StatusBadge status={r.status} /></Table.Td>
                      <Table.Td><Text size="sm">{r.lessonsDone}/{r.lessonsTotal}</Text></Table.Td>
                      <Table.Td><Text size="sm">{r.attemptsUsed}/{r.attemptsLimit}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                  {rows.data!.length === 0 && <Table.Tr><Table.Td colSpan={7}><Text c="dimmed" size="sm" ta="center" py="md">Ничего не найдено</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}
      </QueryState>
      <AssignDrawer opened={opened} onClose={close} />
    </>
  );
}

interface AssignmentDetail extends Row {
  events: AssignmentEvent[];
  notifications: { kind: string; subject: string; sentAt: string | null; error: string | null; createdAt: string }[];
}

const EVENT_RU = { RESCHEDULE: 'Перенос срока', CANCEL: 'Отмена', ADD_ATTEMPTS: 'Добавлены попытки' } as const;

export function AssignmentPage() {
  const id = Number(useParams().id);
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [action, setAction] = useState<'RESCHEDULE' | 'CANCEL' | 'ADD_ATTEMPTS' | 'REASSIGN' | null>(null);
  const [reason, setReason] = useState('');
  const [day, setDay] = useState<string | null>(dayjs().add(7, 'day').format('YYYY-MM-DD'));
  const [n, setN] = useState<number>(1);
  const q = useQuery({ queryKey: ['assignment', id], queryFn: () => api.get<AssignmentDetail>(`/admin/assignments/${id}`) });
  const run = useMutation({
    mutationFn: () => action === 'REASSIGN'
      ? api.post<{ id: number }>(`/admin/assignments/${id}/reassign`, { dueDate: day })
      : api.post(`/admin/assignments/${id}/events`, { type: action, reason, value: action === 'RESCHEDULE' ? day : action === 'ADD_ATTEMPTS' ? String(n) : undefined }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['assignment', id] });
      await qc.invalidateQueries({ queryKey: ['assignments'] });
      setAction(null);
      setReason('');
      notifications.show({ color: 'teal', message: 'Готово, событие записано в журнал' });
      if (action === 'REASSIGN' && r && typeof r === 'object' && 'id' in r) navigate(`/admin/assignments/${(r as { id: number }).id}`);
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });

  return (
    <QueryState loading={q.isLoading} error={q.error}>
      {() => {
        const d = q.data!;
        const own = d.userId === me.id;
        const active = !d.completedAt && !d.cancelledAt;
        return (
          <Stack>
            <Anchor component={Link} to="/admin/assignments" size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> Назначения</Anchor>
            <Group justify="space-between" align="flex-start">
              <div>
                <StatusBadge status={d.status} size="md" />
                <Title order={2} mt={4}>{d.fullName}</Title>
                <Text c="dimmed">{d.department ?? '-'} · {d.email ?? ''} · часовой пояс {d.timezone ?? '-'}</Text>
              </div>
              {!own && (
                <Group gap="xs">
                  {active && <Button variant="light" leftSection={<IconCalendarEvent size={16} />} onClick={() => setAction('RESCHEDULE')}>Перенести срок</Button>}
                  {active && <Button variant="light" color="orange" leftSection={<IconPlus size={16} />} onClick={() => setAction('ADD_ATTEMPTS')}>+N попыток</Button>}
                  {active && <Button variant="subtle" color="gray" leftSection={<IconBan size={16} />} onClick={() => setAction('CANCEL')}>Отменить</Button>}
                  {!active && <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={() => setAction('REASSIGN')}>Назначить повторно</Button>}
                </Group>
              )}
            </Group>
            {own && <Alert color="gray">Это ваше назначение: переносить срок, добавлять попытки и отменять его может только другой ответственный.</Alert>}
            <SimpleGrid cols={{ base: 2, md: 5 }}>
              {[
                ['Курс', `${d.course} · v${d.version}`],
                ['Назначен', fmtDate(d.assignedAt)],
                ['Исходный срок', fmtDateTime(d.originalDueAt, d.timezone)],
                ['Текущий срок', fmtDateTime(d.dueAt, d.timezone)],
                ['Попытки', `${d.attemptsUsed} из ${d.attemptsLimit}`],
              ].map(([l, v]) => <Card key={l} withBorder radius="md" padding="sm"><Text size="xs" c="dimmed">{l}</Text><Text size="sm" fw={600}>{v}</Text></Card>)}
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Card withBorder radius="lg" padding="lg">
                <Title order={5} mb="md">События назначения</Title>
                {d.events.length === 0 ? <Text size="sm" c="dimmed">Событий нет</Text> : (
                  <Timeline bulletSize={20} lineWidth={2}>
                    {d.events.map((e) => (
                      <Timeline.Item key={e.id} title={<Text size="sm" fw={600}>{EVENT_RU[e.type]}{e.type === 'RESCHEDULE' && e.value ? ` → ${fmtDate(e.value, d.timezone)}` : ''}{e.type === 'ADD_ATTEMPTS' ? ` +${e.value}` : ''}</Text>}>
                        <Text size="sm">«{e.reason}»</Text>
                        <Text size="xs" c="dimmed">{e.actorName} · {fmtDateTime(e.occurredAt)}</Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                )}
              </Card>
              <Card withBorder radius="lg" padding="lg">
                <Title order={5} mb="md">Письма</Title>
                {d.notifications.length === 0 ? <Text size="sm" c="dimmed">Писем не было (назначение создано до запуска рассылки)</Text> : (
                  <List size="sm" spacing={6}>
                    {d.notifications.map((m, i) => (
                      <List.Item key={i}>
                        <Text size="sm">{m.kind === 'reminder' ? 'Напоминание' : 'О назначении'}: {m.subject}</Text>
                        <Text size="xs" c={m.error ? 'red' : 'dimmed'}>{m.error ? `не отправлено: ${m.error}` : `отправлено ${fmtDateTime(m.sentAt)}`}</Text>
                      </List.Item>
                    ))}
                  </List>
                )}
              </Card>
            </SimpleGrid>
            <Modal opened={action !== null} onClose={() => setAction(null)} title={action === 'REASSIGN' ? 'Назначить повторно' : action ? EVENT_RU[action] : ''} centered>
              <Stack>
                {(action === 'RESCHEDULE' || action === 'REASSIGN') && <DateInput label="Срок" description={`До конца дня в поясе ${d.timezone}`} value={day} onChange={setDay} minDate={dayjs().format('YYYY-MM-DD')} valueFormat="D MMMM YYYY" />}
                {action === 'ADD_ATTEMPTS' && <NumberInput label="Сколько попыток добавить" min={1} max={5} value={n} onChange={(v) => setN(Number(v) || 1)} />}
                {action === 'RESCHEDULE' && <Text size="xs" c="dimmed">Исходный срок сохранится: метрика и отчёт считаются по нему.</Text>}
                {action !== 'REASSIGN' && <Textarea label="Причина" autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />}
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setAction(null)}>Отмена</Button>
                  <Button disabled={action !== 'REASSIGN' && !reason.trim()} loading={run.isPending} onClick={() => run.mutate()}>Подтвердить</Button>
                </Group>
              </Stack>
            </Modal>
          </Stack>
        );
      }}
    </QueryState>
  );
}
